import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { Input } from "@progress/kendo-react-inputs";
import { useValidation } from "../utils/ValidationProvider";
import "./Password.scss";
import { SafeSVG } from "../svg/SafeSVG";
import { buildUrl } from "../utils/helper";

/**
 * Password component without label
 * Used in the form detail.
 * @module
 * @param {string} [validationMessage] - Validation message if field is invalid
 * @param {string} name - Name
 * @param {bool} [required] - Is input required?
 * @param {func} onChange - Function invoked on value change
 * @param {(string | number)} [value] - Current value
 * @param {bool} [disabled] - Is input readonly?
 * @param {string} [placeholder] - Input placeholder
 * @param {func} [onClick] - Function invoked on click, if input has button
 * @param {bool} [autofocus] - Should input be focused?
 * @param {number} [minLength] - Minimum length
 * @param {number} [maxLength] - Maximum length
 * @param {string} [pattern] - Regex for validity check
 * @param {bool} [readOnly] - Read only
 * @param {number} [tabIndex] - Input tabindex
 * @param {func} [onBlur] - Function invoked on input blur
 * @param {string} [autoComplete] - HTML attribute: autocomplete
 */

const PasswordComponent = ({
  validationMessage,
  validationAllowed,
  name,
  required,
  recommended,
  onChange,
  value,
  disabled,
  readOnly,
  placeholder,
  onClick,
  autofocus,
  minLength,
  maxLength,
  min,
  max,
  pattern,
  tabIndex,
  onBlur,
  valid,
  accept,
  title,
  type,
  step,
  autoComplete
}) => {
  const [inputValue, setInputValue] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [clickActive, setClickActive] = useState(false);

  const field = useMemo(() => ({
    field: name,
    title: title,
    type: type,
    min: min,
    max: max,
    minLength: minLength,
    maxLength: maxLength,
    validationMessage: validationMessage,
    pattern: pattern,
    required: required,
    disabled: disabled,
    recommended: recommended,
    validationAllowed: validationAllowed
  }), [disabled, max, maxLength, min, minLength, name, pattern, required, title, type, validationMessage, recommended, validationAllowed]);

  const { validate, setValidationValue } = useValidation(field, value);

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  const handleBlur = () => {
    validate();
    if (onBlur) {
      onBlur();
    }
  };

  const handleChange = (e) => {
    const inputValue = e.target.value.replace(/[\u200B-\u200D\uFEFF]/g, '');
    setInputValue(inputValue);
    onChange(name, inputValue);
    setValidationValue(inputValue);
  };

  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  return (
    <>
      <Input
        data-testid={name || title}
        className={`form-input input ${disabled ? "disabled-field" : required ? "required-field" : (recommended ? "recommended-field" : "default-field")}`}
        validationMessage={validationMessage || "Povinné"}
        required={required}
        onChange={handleChange}
        value={inputValue}
        type={!showPassword ? "password" : "text"}
        placeholder={placeholder}
        onClick={onClick}
        autoFocus={autofocus}
        minLength={minLength}
        maxLength={maxLength}
        min={min}
        max={max}
        tabIndex={tabIndex}
        onBlur={handleBlur}
        readOnly={disabled || readOnly}
        valid={valid}
        accept={accept}
        title={title}
        step={step}
        autoComplete={autoComplete || "new-password"}
      />
      {value && (
        <div className="showPassword">
          <div
            title={showPassword ? "Zobraziť heslo" : "skryť heslo"}
            onClick={() => setClickActive(!clickActive)}
            onMouseDown={(e) => e.preventDefault()}
            onMouseUp={clickActive ? () => setShowPassword(true) : undefined}
            onMouseLeave={
              !clickActive ? () => handleTogglePassword(false) : undefined
            }
            onMouseEnter={
              !clickActive ? () => setShowPassword(true) : undefined
            }
            id="maskValue"
            className={"showPassword-eye"}>
            <SafeSVG src={buildUrl(showPassword ? "assets/icons/siforvane-heslo.svg" : "assets/icons/viditelne-heslo.svg")} />
          </div>
        </div>
      )}
    </>
  );
};

PasswordComponent.propTypes = {
  validationMessage: PropTypes.string,
  name: PropTypes.string.isRequired,
  required: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  placeholder: PropTypes.string,
  onClick: PropTypes.func,
  autofocus: PropTypes.bool,
  minLength: PropTypes.number,
  maxLength: PropTypes.number,
  min: PropTypes.number,
  max: PropTypes.number,
  pattern: PropTypes.string,
  tabIndex: PropTypes.number,
  isPicker: PropTypes.bool,
  onBlur: PropTypes.func,
  valid: PropTypes.bool,
  accept: PropTypes.string,
  title: PropTypes.string,
  step: PropTypes.string,
  multiple: PropTypes.bool,
  autoComplete: PropTypes.string
};

export default PasswordComponent;
